import { useState } from 'react';
import SafeIcon from './SafeIcon';
import PlatformSlotSettings from './PlatformSlotSettings';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { workspaces } from '../store/seed';

const DELAY_BEFORE_SCROLLING_TO_LIMITS_IN_MILLISECONDS = 60;

export default function PlatformHome() {
  const { translate, formatCount, language } = useApp();
  const { showPlannedFeatureNotice } = useUi();
  const [expandedAgency, setExpandedAgency] = useState<string | null>(null);
  const [limitsFor, setLimitsFor] = useState<string>('brzoza');
  const viewLimits = (id: string) => {
    setLimitsFor(id);
    window.setTimeout(
      () =>
        document
          .getElementById('platform-limits')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      DELAY_BEFORE_SCROLLING_TO_LIMITS_IN_MILLISECONDS
    );
  };
  const maxVideoLength = new Intl.NumberFormat(language, {
    style: 'unit',
    unit: 'second',
    unitDisplay: 'long',
  }).format(120);
  const renderMinutesAlertThreshold = new Intl.NumberFormat(language, { style: 'percent' }).format(
    0.8
  );
  const accessedAt = new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(2026, 10, 3, 14, 22));
  const agencies: [string, number][] = [
    ['Hakusana', 12],
    ['Nordic Social Studio', 8],
  ];
  return (
    <div className="content-stack">
      <div>
        <h1>{translate('Platform overview')}</h1>
        <p className="subtitle">{translate('Platform settings and operational health.')}</p>
      </div>
      <div className="notice-banner">
        <SafeIcon name="FiAlertTriangle" />
        <div>
          <strong>{translate('Provider incident · TikTok media processing')}</strong>
          <div className="muted">
            {translate('Some video revisions may take longer than usual.')}
          </div>
        </div>
        <button
          className="button small"
          onClick={() => showPlannedFeatureNotice('Incident details')}
        >
          {translate('View details')}
        </button>
      </div>
      <div className="panel">
        <h2>{translate('Agencies')}</h2>
        {agencies.map(([agency, workspaceCount]) => {
          const isExpanded = expandedAgency === agency;
          const toggleAgency = () =>
            agency === 'Hakusana'
              ? setExpandedAgency(isExpanded ? null : agency)
              : showPlannedFeatureNotice('Manage an agency');
          return (
            <div className="checklist-item" key={agency}>
              <span className="agency-mark">{agency[0]}</span>
              <strong style={{ flex: 1 }}>{agency}</strong>
              <span className="muted">{formatCount(workspaceCount, 'workspace')}</span>
              <span className="status published">{translate('agencyActive')}</span>
              <button className="button small" aria-expanded={isExpanded} onClick={toggleAgency}>
                {translate(isExpanded ? 'Close' : 'Manage')}
              </button>
            </div>
          );
        })}
        {expandedAgency === 'Hakusana' && (
          <div className="panel inset">
            <h3>{translate('Hakusana workspaces')}</h3>
            {workspaces.map((workspace) => (
              <div className="checklist-item" key={workspace.id}>
                <strong style={{ flex: 1 }}>{workspace.name}</strong>
                <span className="muted">
                  {workspace.city} · {workspace.channels.join(', ')}
                </span>
                <button className="button small" onClick={() => viewLimits(workspace.id)}>
                  {translate('View limits')}
                </button>
              </div>
            ))}
            <p className="muted small-print">
              {translate('…and {n} more workspaces that are not part of this demo.', { n: 10 })}
            </p>
          </div>
        )}
      </div>
      <div className="panel">
        <h2>{translate('Modules')}</h2>
        <div className="checklist-item">
          <strong style={{ flex: 1 }}>{translate('Social media')}</strong>
          <span className="status published">{translate('On')}</span>
          <button
            className="button small"
            onClick={() => showPlannedFeatureNotice('Configure the Social Media module')}
          >
            {translate('Configure')}
          </button>
        </div>
        <div className="checklist-item">
          <strong style={{ flex: 1 }}>{translate('Email')}</strong>
          <span className="status neutral">{translate('Coming soon')}</span>
        </div>
      </div>
      <PlatformSlotSettings workspaceId={limitsFor} onWorkspaceChange={setLimitsFor} />
      <div className="two-column">
        <div className="panel">
          <h2>{translate('Media operations')}</h2>
          <p>
            {translate('Max video length')}: <strong>{maxVideoLength}</strong>
          </p>
          <p>
            {translate('Render-minutes alert')}:{' '}
            <strong>
              {translate('{value} per workspace', { value: renderMinutesAlertThreshold })}
            </strong>
          </p>
          <p>
            {translate('Storage')}:{' '}
            <strong>{translate('Originals deleted after processing')}</strong>
          </p>
          <p>
            {translate('Copies')}: <strong>{translate('Drive-linked or stored copies')}</strong>
          </p>
          <button className="button" onClick={() => showPlannedFeatureNotice('Edit media limits')}>
            {translate('Edit limits')}
          </button>
        </div>
        <div className="panel">
          <h2>{translate('Support access log')}</h2>
          <div className="checklist-item">
            <SafeIcon name="FiEye" />
            <div>
              <strong>{translate('Hakusana support viewed Brzoza Coffee')}</strong>
              <div className="muted">
                {accessedAt} · {translate('{name} approved access', { name: 'Anna Nowak' })}
              </div>
            </div>
          </div>
          <button
            className="button small"
            onClick={() => showPlannedFeatureNotice('Support access log')}
          >
            {translate('View access log')}
          </button>
        </div>
      </div>
    </div>
  );
}
